// Matches `{{placeholder}}` and `%{placeholder}`.
const interpolationPattern = /(?:{{|%{)(.*?)}}?/gm;

/**
 * Class for returning translations from the compiled locale files.
 */
class i18n {
  /**
   * Interpolates all variables in the given message.
   *
   * @param {string} message the translated message
   * @param {object} options key/values for interpolation
   * @returns {string} The translated message
   * @example
   *   i18n.interpolate("The coupon code '%{id}' is not valid", { id: "FREEMONTH" });
   *   // => "The coupon code 'FREEMONTH' is not valid"
   */
  static interpolate(message, options) {
    const placeholders = message.match(interpolationPattern);

    if (placeholders == null) {
      return message;
    }

    let key;
    let regex;
    let value;
    for (const placeholder of placeholders) {
      key = placeholder.replace(interpolationPattern, "$1");

      if (Object.prototype.hasOwnProperty.call(options, key)) {
        value = options[key];
      } else if (process.env.RAILS_ENV !== "production") {
        throw new Error("Missing i18n param: `" + key + "`");
      }

      regex = new RegExp(placeholder.replace(/{/gm, "\\{").replace(/}/gm, "\\}"));
      message = message.replace(regex, value);
    }

    return message;
  }

  /**
   * Returns the URL path from the compiled list of Rails paths.
   *
   * @param {string} pathName the route's name
   * @param {object} params key/values for path params
   * @returns {string} The URL path
   * @example
   *   i18n.path("features", { locale: "es" });
   *   // => "/es/features"
   *   i18n.path("edit_user", { id: 1, format: "html" });
   *   // => "/en/users/1/edit.html"
   */
  static path(pathName, params = {}) {
    let path = i18n.paths[pathName];

    if (path == null) {
      if (process.env.RAILS_ENV !== "production") {
        throw new Error("Missing URL path name: `" + pathName + "`");
      }

      return (
        "/analytics/missing_i18n_path" +
        `?path=${pathName}` +
        `&params=${encodeURI(JSON.stringify(params))}` +
        `&pathname=${encodeURI(window.location.pathname)}` +
        `&js_locale=${params.locale || i18n.locale}`
      );
    }

    params.locale = params.locale || i18n.locale;
    path = i18n.interpolate(path, params);

    if (params.format != null) {
      path += `.${params.format}`;
    }

    return path;
  }

  /**
   * Returns the translation for the given translation key with optional interpolation key/values.
   *
   * @param {string} key the translation key
   * @param {object} options key/values for interpolation
   * @returns {string} The translation value
   * @example
   *   i18n.translate("global.log_in");
   *   // => "Log In"
   *   i18n.translate("credit_card.coupon_code_invalid", { id: "FREEMONTH" });
   *   // => "The coupon code 'FREEMONTH' is not valid"
   */
  static translate(key, options) {
    let message = i18n.translations[key];

    if (message == null) {
      if (process.env.RAILS_ENV !== "production") {
        throw new Error("Missing translation: `" + key + "`");
      }

      return "Missing translation: `" + key + "`";
    }

    // If no options are given, no interpolation needed
    if (options == null) {
      return message;
    }

    // If the message is an object, it has multiple definitions for pluralization
    if (typeof message === "object") {
      if (options.count === 1) {
        message = message.one;
      } else {
        message = message.other;
      }
    }

    return i18n.interpolate(message, options);
  }

  /**
   * Returns the full URL from the compiled list of Rails paths.
   *
   * @param {string} pathName the route's name
   * @param {object} params key/values for path params
   * @returns {string} The full URL
   * @example
   *   i18n.url("edit_user", { id: 1 });
   *   // => "https://www.simpleinout.com/en/users/1/edit"
   *   i18n.path("features", { locale: "es" });
   *   // => "https://www.simpleinout.com/es/features"
   */
  static url(pathName, params = {}) {
    const path = i18n.path(pathName, params);

    return `${window.location.protocol}//${window.location.host}${path}`;
  }
}

i18n.paths = {};
i18n.translations = {};

/**
 * Alias for i18n.translate.
 *
 * @function t
 * @alias translate
 */
i18n.t = i18n.translate;

export default i18n;
